import { useRef } from 'react';
import { Table as TRTable } from '@tanstack/react-table';
import { UploadButton, DownloadButton } from '@anytag/shared-atoms';
import { ListWithPagination, UploadCsvModal } from '@anytag/shared-molecules';
import { useDownloadFile, useToggleState } from '@anytag/shared-hooks';
import { InstagramFeedPost, useAllInstagramFeedPostsQuery } from '@anytag/graphql';
import {
  getOffset,
  getUploadApiUrl,
  getValuableVariables,
  convertOrderToORDER,
  getAccountManagementApiUrl
} from '@anytag/shared-utils';
import { ProfilePostsCount, ProfilePostsFilter, AnalyticsInstagramPostsList } from '@anytag/shared-organisms';
import { useAnalyticsIgComparePostsFilter } from '../analytics-instagram-compare-posts/hooks';

export interface AnalyticsInstagramPostsTemplateProps {
  isComparePage?: boolean;
  mainSocialAccountId: number;
}

export const AnalyticsInstagramPostsTemplate = ({
  isComparePage,
  mainSocialAccountId
}: AnalyticsInstagramPostsTemplateProps) => {
  const uploadModal = useToggleState();
  const tableRef = useRef<TRTable<InstagramFeedPost>>(null);
  const { downloading, handleDownloadFile } = useDownloadFile();
  const { page, limit, sort, filter, setFilter } = useAnalyticsIgComparePostsFilter();
  const { data, previousData, loading, refetch } = useAllInstagramFeedPostsQuery({
    skip: !mainSocialAccountId,
    variables: getValuableVariables({
      limit,
      offset: getOffset(page, limit),
      accountId: Number(mainSocialAccountId),
      orderBy: { field: sort.field, order: convertOrderToORDER(sort.order) },
      ...filter
    })
  });

  const result = loading ? previousData : data;
  const totalRecords = result?.allInstagramFeedPostsCount?.totalNumber;

  return (
    <>
      <ListWithPagination pagination={{ totalRecords }}>
        <ProfilePostsFilter filter={filter} setFilter={setFilter} hasPeriod />

        <ProfilePostsCount count={totalRecords}>
          <UploadButton onClick={uploadModal.open} css={{ marginRight: '8px' }} />
          <DownloadButton
            loading={downloading}
            onClick={() =>
              handleDownloadFile({
                params: getValuableVariables({
                  endDate: filter.endDate,
                  startDate: filter.startDate,
                  selectedColumns: tableRef.current
                    ?.getVisibleFlatColumns()
                    .reduce<
                      string[]
                    >((acc, curr) => (curr.columnDef.meta?.sortField && curr.id ? [...acc, curr.id] : acc), [])
                }),
                filenamePrefix: 'feedPosts',
                url: getUploadApiUrl(`analytics/feed_posts/${mainSocialAccountId}/csv/custom`)
              })
            }
          />
        </ProfilePostsCount>

        <AnalyticsInstagramPostsList
          loading={loading}
          tableRef={tableRef}
          isComparePage={isComparePage}
          data={result?.allInstagramFeedPosts || []}
        />
      </ListWithPagination>

      <UploadCsvModal
        onFileUploaded={refetch}
        open={uploadModal.status}
        onClose={uploadModal.close}
        uploadFile={{
          fileNamePrefix: 'feed_posts.csv',
          url: getAccountManagementApiUrl(`api/analytics/feed_posts/${mainSocialAccountId}/csv`)
        }}
        downloadFile={{
          extension: '.csv',
          filenamePrefix: 'feed_posts',
          params: { startDate: filter.startDate, endDate: filter.endDate },
          url: getUploadApiUrl(`analytics/feed_posts/${mainSocialAccountId}/csv`)
        }}
      />
    </>
  );
};
